import { Layer } from '../../../types/design';
import { BackgroundSettings } from '../../../components/template-editor/BackgroundPropertiesPanel';

export const getImageVisibleBounds = (
  containerWidth: number,
  containerHeight: number,
  naturalWidth: number,
  naturalHeight: number,
  objectFit: 'cover' | 'contain' | 'fill' = 'contain'
) => {
  if (objectFit === 'fill') {
    return { left: 0, top: 0, width: containerWidth, height: containerHeight };
  }

  const containerRatio = containerWidth / containerHeight;
  const imageRatio = naturalWidth / naturalHeight;

  let width, height, left, top;

  if (objectFit === 'contain') {
    if (containerRatio > imageRatio) {
      height = containerHeight;
      width = height * imageRatio;
      top = 0;
      left = (containerWidth - width) / 2;
    } else {
      width = containerWidth;
      height = width / imageRatio;
      left = 0;
      top = (containerHeight - height) / 2;
    }
  } else { // cover
    if (containerRatio > imageRatio) {
      width = containerWidth;
      height = width / imageRatio;
      left = 0;
      top = (containerHeight - height) / 2;
    } else {
      height = containerHeight;
      width = height * imageRatio;
      top = 0;
      left = (containerWidth - width) / 2;
    }
  }

  return { left, top, width, height };
};

export const findLayerAtPosition = (
  layers: Layer[],
  x: number,
  y: number,
  layerPositions: Record<string, { left: number; top: number; width?: number; height?: number }>,
  canvasRect?: DOMRect,
  scale = 1
): Layer | null => {
  // Search in reverse z-index order (top to bottom)
  for (let i = layers.length - 1; i >= 0; i--) {
    const layer = layers[i];
    const pos = layerPositions[layer.id];

    // Check children first (they are on top of parent)
    if (layer.children) {
      const found = findLayerAtPosition(layer.children, x, y, layerPositions, canvasRect, scale);
      if (found) return found;
    }

    if (pos && pos.width !== undefined && pos.height !== undefined) {
      if (x >= pos.left && x <= pos.left + pos.width &&
        y >= pos.top && y <= pos.top + pos.height) {
        return layer;
      }
    }
  }
  return null;
};

/**
 * Calculates the new top-left coordinates (left, top) required to keep a specific anchor point
 * fixed in world space when dimensions (width, height) change, accounting for the center-based rotation origin.
 * 
 * @param initialPos Initial position, size and rotation
 * @param deltaW Change in width
 * @param deltaH Change in height
 * @param anchorX Normalized anchor X coordinate (0 = left, 0.5 = center, 1 = right)
 * @param anchorY Normalized anchor Y coordinate (0 = top, 0.5 = center, 1 = bottom)
 */
export const calculateAnchoredPosition = (
  initialPos: { left: number, top: number, width: number, height: number, rotation: number },
  deltaW: number,
  deltaH: number,
  anchorX: number,
  anchorY: number
) => {
  const rad = (initialPos.rotation || 0) * (Math.PI / 180);
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);

  // Shift logic:
  // dx_center_rel = (1/2 - anchorX) * deltaW
  // dy_center_rel = (1/2 - anchorY) * deltaH
  const localShiftX = (0.5 - anchorX) * deltaW;
  const localShiftY = (0.5 - anchorY) * deltaH;

  // Transform local center shift to world center shift
  const worldShiftX = localShiftX * cos - localShiftY * sin;
  const worldShiftY = localShiftX * sin + localShiftY * cos;

  // New center must be: C' = C + worldShift
  // Since C = (L + W/2, T + H/2)
  // L' = L + worldShiftX - deltaW/2
  // T' = T + worldShiftY - deltaH/2
  return {
    left: initialPos.left + worldShiftX - deltaW / 2,
    top: initialPos.top + worldShiftY - deltaH / 2
  };
};

export const removeLayersRecursive = (layers: Layer[], idsToRemove: string[]): Layer[] => {
  return layers.filter(l => !idsToRemove.includes(l.id)).map(l => {
    if (l.children) {
      return { ...l, children: removeLayersRecursive(l.children, idsToRemove) };
    }
    return l;
  });
};

export const updateLockRecursive = (layers: Layer[], idsToUpdate: string[], locked: boolean): Layer[] => {
  return layers.map(l => {
    if (idsToUpdate.includes(l.id)) {
      const updated = { ...l, locked };
      if (updated.children) {
        updated.children = updateLockRecursive(updated.children, updated.children.map(c => c.id), locked);
      }
      return updated;
    }
    if (l.children) {
      return { ...l, children: updateLockRecursive(l.children, idsToUpdate, locked) };
    }
    return l;
  });
};

// Helper to apply background settings (shared between root canvas and layers)
export const applyBackgroundSettings = (settings: BackgroundSettings | undefined): React.CSSProperties => {
  if (!settings) return {};
  const bgStyle: React.CSSProperties = {};

  if (settings.type === 'image' && settings.imageUrl) {
    bgStyle.backgroundImage = `url(${settings.imageUrl})`;
    bgStyle.backgroundSize = settings.imageFit;
    bgStyle.backgroundPosition = 'center';
    bgStyle.backgroundRepeat = 'no-repeat';
  } else if (settings.type === 'gradient') {
    // Use backgroundImage for gradients to avoid clobbering other background properties
    bgStyle.backgroundImage = `linear-gradient(${settings.gradientAngle || 0}deg, ${settings.gradientStart || '#ffffff'}, ${settings.gradientEnd || '#000000'})`;
  } else {
    bgStyle.backgroundColor = settings.solidColor || 'transparent';
  }

  // Apply opacity
  const settingsOpacity = !isNaN(Number(settings.opacity)) ? Number(settings.opacity) / 100 : 1;
  bgStyle.opacity = settingsOpacity;

  if (settings.blur > 0) {
    bgStyle.filter = `blur(${settings.blur}px)`;
  }

  // Apply color overlay (using inset box shadow)
  if (settings.overlayOpacity > 0 && settings.overlayColor) {
    const alpha = Math.round((settings.overlayOpacity / 100) * 255).toString(16).padStart(2, '0');
    const colorWithAlpha = `${settings.overlayColor}${alpha}`;
    bgStyle.boxShadow = `inset 0 0 0 1000px ${colorWithAlpha}`;
  }

  return bgStyle;
};

/**
 * Unified normalization helper to ensure all template data sources (API, saved, legacy)
 * are transformed into a consistent modern format for the editor.
 */
export const normalizeTemplateData = (data: any) => {
  if (!data) return null;

  // 1. Unwrap nested data/fullData if present
  let normalized = { ...data };
  if (data.fullData) {
    normalized = { ...normalized, ...data.fullData };
  } else if (data.data) {
    try {
      const parsedData = typeof data.data === 'string' ? JSON.parse(data.data) : data.data;
      const { id: staleId, ...cleanParsedData } = parsedData;
      normalized = { ...normalized, ...cleanParsedData, originTemplateId: normalized.originTemplateId || staleId };
    } catch (e) {
      console.warn('[Editor] Failed to parse nested dynamic data', e);
    }
  }

  // 1.5 Special check for 'background' if not unwrapped
  if (!normalized.background && data.background) {
    normalized.background = data.background;
  }

  // 2. Extract and sanitize layers
  const layers = normalized.layers || normalized.dynamicLayers || normalized.elements || [];
  normalized.dynamicLayers = JSON.parse(JSON.stringify(layers));

  // 3. Heal Background Settings if primary 'background' field exists but settings are missing
  const backgroundSettings = JSON.parse(JSON.stringify(normalized.backgroundSettings || {}));
  
  if (!backgroundSettings.root) {
    let healedFromSource = false;

    // Path A: Heal from legacy 'background' field
    if (normalized.background) {
      const bg = normalized.background;
      backgroundSettings.root = {
        type: bg.type === 'solid' ? 'solid' : bg.type === 'image' ? 'image' : bg.type === 'gradient' ? 'gradient' : 'solid',
        solidColor: bg.type === 'solid' ? bg.value : (bg.type === 'gradient' ? bg.value : '#FFFFFF'),
        imageUrl: bg.type === 'image' ? bg.value : undefined,
        opacity: 100,
        gradientStart: bg.type === 'gradient' ? bg.value : '#FFFFFF',
        gradientEnd: '#000000',
        gradientAngle: 0,
        imageFit: 'cover',
        blur: 0,
        overlayColor: '#000000',
        overlayOpacity: 0
      };
      healedFromSource = true;
    }

    // Path B: Fallback - Extract from root layer className (e.g., bg-[#54311D])
    if (!healedFromSource && normalized.dynamicLayers) {
      const rootLayer = normalized.dynamicLayers.find((l: any) => l.id === 'root');
      if (rootLayer && rootLayer.className) {
        const hexMatch = rootLayer.className.match(/bg-\[#([A-Fa-f0-9]{3,6})\]/);
        if (hexMatch) {
          const hexColor = hexMatch[0].replace('bg-[', '').replace(']', '');
          backgroundSettings.root = {
            type: 'solid',
            solidColor: hexColor,
            opacity: 100,
            gradientStart: hexColor,
            gradientEnd: '#000000',
            gradientAngle: 0,
            imageFit: 'cover',
            blur: 0,
            overlayColor: '#000000',
            overlayOpacity: 0
          };
        }
      }
    }
  }
  normalized.backgroundSettings = backgroundSettings;

  // 4. Propagate healed background to pages if they exist
  const textStyles = normalized.textStyles || {};
  const imageSettings = normalized.imageSettings || {};
  const scrollerSettings = normalized.scrollerSettings || {};

  const syncBindingsFromLayers = (ls: any[]) => {
    ls.forEach(l => {
      if (l.binding) {
        if (l.type === 'text') {
          textStyles[l.id] = { ...(textStyles[l.id] || {}), binding: l.binding };
        } else if (l.type === 'image') {
          imageSettings[l.id] = { ...(imageSettings[l.id] || {}), binding: l.binding };
        } else if (l.type === 'scroller' || l.isApp) {
          scrollerSettings[l.id] = { ...(scrollerSettings[l.id] || {}), binding: l.binding };
        }
      }
      if (l.children) {
        syncBindingsFromLayers(l.children);
      }
    });
  };

  if (normalized.dynamicLayers) {
    syncBindingsFromLayers(normalized.dynamicLayers);
  }

  normalized.textStyles = textStyles;
  normalized.imageSettings = imageSettings;
  normalized.scrollerSettings = scrollerSettings;

  if (normalized.pages && normalized.pages.length > 0) {
    normalized.pages = normalized.pages.map((p: any) => {
      const pageTextStyles = p.textStyles || {};
      const pageImageSettings = p.imageSettings || {};
      const pageScrollerSettings = p.scrollerSettings || {};

      const syncPageBindings = (ls: any[]) => {
        ls.forEach(l => {
          if (l.binding) {
            if (l.type === 'text') {
              pageTextStyles[l.id] = { ...(pageTextStyles[l.id] || {}), binding: l.binding };
            } else if (l.type === 'image') {
              pageImageSettings[l.id] = { ...(pageImageSettings[l.id] || {}), binding: l.binding };
            } else if (l.type === 'scroller' || l.isApp) {
              pageScrollerSettings[l.id] = { ...(pageScrollerSettings[l.id] || {}), binding: l.binding };
            }
          }
          if (l.children) {
            syncPageBindings(l.children);
          }
        });
      };

      const pageLayers = p.dynamicLayers || p.layers || [];
      syncPageBindings(pageLayers);

      return {
        ...p,
        textStyles: pageTextStyles,
        imageSettings: pageImageSettings,
        scrollerSettings: pageScrollerSettings,
        backgroundSettings: p.backgroundSettings && p.backgroundSettings.root 
          ? p.backgroundSettings 
          : { ...backgroundSettings }
      };
    });
  }

  // 5. Extract text contents if missing
  if (!normalized.textContents || Object.keys(normalized.textContents).length === 0) {
    const contents: Record<string, string> = {};
    const extractText = (ls: any[]) => {
      ls.forEach(l => {
        if (l.type === 'text' && l.content) contents[l.id] = l.content;
        if (l.children) extractText(l.children);
      });
    };
    extractText(normalized.dynamicLayers);
    normalized.textContents = contents;
  }

  // 5. Apply defaults for all settings objects to prevent UI crashes
  normalized.layerPositions = { ...(normalized.layerPositions || {}) };
  normalized.textStyles = { ...(normalized.textStyles || {}) };
  normalized.imageSettings = { ...(normalized.imageSettings || {}) };
  normalized.shapeSettings = { ...(normalized.shapeSettings || {}) };
  normalized.scrollerSettings = { ...(normalized.scrollerSettings || {}) };
  normalized.widgetSettings = { ...(normalized.widgetSettings || {}) };
  normalized.templateSettings = { ...(normalized.templateSettings || {}) };
  normalized.templateSettings.opacity = normalized.templateSettings.opacity ?? 100;
  normalized.templateSettings.duration = normalized.templateSettings.duration ?? 20000;
  normalized.templateSettings.flipHorizontal = normalized.templateSettings.flipHorizontal ?? false;
  normalized.templateSettings.flipVertical = normalized.templateSettings.flipVertical ?? false;
  normalized.templateSettings.crop = normalized.templateSettings.crop || { enabled: false, top: 0, right: 0, bottom: 0, left: 0 };
  normalized.templateSettings.animation = normalized.templateSettings.animation || { type: 'none', duration: 1000, delay: 0, easing: 'ease' };

  return normalized;
};
